import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import { User } from 'firebase/auth';

import { TEST_CONSTANTS } from '../constants';
import { ROUTES } from '../routes';
import {
  AdaptivePage,
  AdaptivePageSubmit,
  AdaptiveVocabularyTest,
  AdaptiveVocabularyTestSchema,
  AdaptiveVocabularyTestResult,
  AdaptiveVocabularyTestResultSchema,
  PageSchema,
  VocabTestCreateInput,
  AdaptiveUserProfile,
  AdaptiveSharedVocabTestResults,
  SharedVocabTestResultsSchema,
  VocabTestConceptsSchema,
} from '../utils/adaptiveModels';
import { getReferrerFromCookie } from '../utils/referrerCookie';
import { clearAdaptiveTestFromLocalStorage } from './adaptiveVocabTestStorage';
import { clearVocabResultData, type UnknownWord } from './vocabResultData';

const DEFAULT_API_URL = 'https://api.glite.ai/l2';

export const ADAPTIVE_TEST_QUERY_KEY = 'adaptiveVocabTest';
export const ADAPTIVE_PAGE_QUERY_KEY = 'adaptiveVocabTestPage';

const apiClient = axios.create({
  baseURL: getBaseURL(),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(
  (config) => {
    config.headers.Platform = 'web';
    config.headers['Client-Id'] = 'adaptive-vocab-test';
    config.headers['Client-Version'] = `${TEST_CONSTANTS.ADAPTIVE_VERSION}.0.0`;
    config.headers['Request-ID'] ??= crypto.randomUUID();
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    Sentry.captureException(error);
    return Promise.reject(error);
  },
);

axiosRetry(apiClient, {
  retries: 2,
  retryDelay: (...args) => axiosRetry.exponentialDelay(...args),
});

function getBaseURL(): string {
  return process.env.GLITE_API_URL || DEFAULT_API_URL;
}

function getEndpointPath(path: string): string {
  return `/v1/adaptive_vocab_tests${path}`;
}

async function getAuthHeaders(user: User | null): Promise<Record<string, string> | undefined> {
  if (!user) return undefined;
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

function resetMissingAdaptiveTest(): void {
  clearAdaptiveTestFromLocalStorage();
  clearVocabResultData();
  // Full-page navigation (not react-router), so it must include the deploy
  // basename. BASE_URL ends with "/", so strip the route's leading slash.
  window.location.assign(`${import.meta.env.BASE_URL}${ROUTES.TEST_ROOT.slice(1)}`);
}

function handleAdaptiveTestNotFound(error: unknown): void {
  if (axios.isAxiosError(error) && error.response?.status === 404) {
    resetMissingAdaptiveTest();
  }
}

/**
 * `POST /`. For `batch_words` vocab tests the returned envelope carries no questions
 * (`items` and `responses` are empty): the endpoint is shared with the iOS
 * client, whose flat item schema cannot represent headword-translation
 * questions, so the first page is fetched separately with `getCurrentPage`.
 * This workaround can be removed once the shared endpoints are adapted to the
 * page-based vocab test on all platforms.
 */
async function createAdaptiveVocabTest(
  input: VocabTestCreateInput,
): Promise<AdaptiveVocabularyTest> {
  const referrer = getReferrerFromCookie();
  const payload = {
    ...input,
    referred_by: referrer || undefined,
    onboarding: false,
  };

  const response = await apiClient.post(getEndpointPath('/'), payload);

  if (response.status === 201) {
    console.log('[API] Created new adaptive vocab test:', response.data);
    return AdaptiveVocabularyTestSchema.parse(response.data);
  }

  throw new Error('Failed to create adaptive vocab test');
}

/**
 * `GET /{test_id}/pages/`: the page to answer next — the still-pending one, or
 * a freshly generated one when every served page is answered. Used right after
 * the vocab test is created and when the user returns to an in-progress vocab test. An
 * empty `questions` list means the vocab test is over.
 */
async function getCurrentPage(testId: string): Promise<AdaptivePage> {
  let response;
  try {
    response = await apiClient.get(getEndpointPath(`/${testId}/pages/`));
  } catch (error) {
    handleAdaptiveTestNotFound(error);
    throw error;
  }

  if (response.status === 200) {
    return PageSchema.parse(response.data);
  }

  throw new Error('Failed to get the current vocab test page');
}

/**
 * `POST /{test_id}/pages/`: submit the pending page's answers and receive the
 * next page. The backend decides what comes next (a self-reporting batch or a
 * headword-translation page) and rejects re-answering an answered page.
 */
async function submitPage(testId: string, payload: AdaptivePageSubmit): Promise<AdaptivePage> {
  let response;
  try {
    response = await apiClient.post(getEndpointPath(`/${testId}/pages/`), payload);
  } catch (error) {
    handleAdaptiveTestNotFound(error);
    throw error;
  }

  if (response.status === 202) {
    return PageSchema.parse(response.data);
  }

  throw new Error('Failed to submit the vocab test page');
}

async function updateAdaptiveProfile(
  testId: string,
  profile: AdaptiveUserProfile,
  user: User | null = null,
): Promise<AdaptiveVocabularyTest> {
  const headers = await getAuthHeaders(user);
  let response;
  try {
    response = await apiClient.put(getEndpointPath(`/${testId}/profile/`), profile, {
      headers,
    });
  } catch (error) {
    handleAdaptiveTestNotFound(error);
    throw error;
  }

  if (response.status === 202) {
    return AdaptiveVocabularyTestSchema.parse(response.data);
  }

  throw new Error('Failed to update profile');
}

async function completeAdaptiveVocabTest(
  testId: string,
  user: User | null,
): Promise<AdaptiveVocabularyTestResult> {
  const headers = await getAuthHeaders(user);

  console.log('[API] Calling complete:', { testId });

  let response;
  try {
    // The web flow does not use the vocab-test collection, so opt out of the
    // backend's auto-create behavior.
    response = await apiClient.post(
      getEndpointPath(`/${testId}/complete/?skip_collection_creation=true`),
      undefined,
      { headers },
    );
  } catch (error) {
    handleAdaptiveTestNotFound(error);
    throw error;
  }

  console.log('[API] Complete response:', response.status);

  if (response.status === 202) {
    try {
      const parsed = AdaptiveVocabularyTestResultSchema.parse(response.data);
      console.log('[API] Parsed successfully:', parsed.test_id);
      return parsed;
    } catch (parseError) {
      console.error('[API] Failed to parse response:', parseError);
      console.error('[API] Response data:', JSON.stringify(response.data, null, 2));
      throw parseError;
    }
  }

  throw new Error(`Failed to complete vocab test: ${response.status}`);
}

/**
 * Fetches the unknown words of a completed test from the same endpoint the
 * iOS client uses when it prepares the vocab-test collection
 * (GET /{test_id}/concepts/). The backend restricts the result to the
 * adaptive subset and the idioms — fakes and the other verification items are
 * excluded — so the web results funnel stays coherent with what the app shows.
 */
export async function fetchAdaptiveTestUnknownWords(
  testId: string,
  user: User,
): Promise<UnknownWord[]> {
  const headers = await getAuthHeaders(user);
  const response = await apiClient.get(getEndpointPath(`/${testId}/concepts/`), { headers });

  if (response.status !== 200) {
    throw new Error(`Failed to fetch vocab test concepts: ${response.status}`);
  }

  const concepts = VocabTestConceptsSchema.parse(response.data);
  return concepts.unknown.map((concept) => ({
    headword: concept.text,
    definition: concept.short_description ?? concept.description ?? '',
    tags: concepts.question_tags[concept.id] ?? [],
  }));
}

async function fetchSharedAdaptiveResults(
  shortcode: string,
): Promise<AdaptiveSharedVocabTestResults> {
  const response = await apiClient.get(getEndpointPath(`/share/${shortcode}/`));

  if (response.status === 200) {
    return SharedVocabTestResultsSchema.parse(response.data);
  }

  throw new Error('Failed to fetch shared results');
}

export function useCreateAdaptiveVocabTest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAdaptiveVocabTest,
    onMutate: () => {
      queryClient.removeQueries({ queryKey: [ADAPTIVE_TEST_QUERY_KEY] });
      queryClient.removeQueries({ queryKey: [ADAPTIVE_PAGE_QUERY_KEY] });
    },
    onSuccess: (data) => {
      queryClient.setQueryData([ADAPTIVE_TEST_QUERY_KEY, data.test_id], data);
    },
  });
}

export function useCurrentPage(testId: string | null) {
  return useQuery({
    queryKey: [ADAPTIVE_PAGE_QUERY_KEY, testId],
    queryFn: () => getCurrentPage(testId!),
    enabled: !!testId,
    staleTime: Infinity,
  });
}

/**
 * Re-fetch the page the server is waiting for. Used after the server refuses a
 * submission (HTTP 400): the page on screen is not the pending one any more —
 * typically it was already answered — so the client falls back in line with
 * the backend, which is the only source of truth for the page sequence.
 */
export function useRefetchCurrentPage() {
  const queryClient = useQueryClient();
  return (testId: string) =>
    queryClient.invalidateQueries({ queryKey: [ADAPTIVE_PAGE_QUERY_KEY, testId] });
}

/**
 * Fetch the current page anew and wait for it, replacing whatever is cached.
 * Used after something that changes how the pending page is served (the
 * user's language), before going back to the question screen.
 */
export function useReloadCurrentPage() {
  const queryClient = useQueryClient();
  return (testId: string) =>
    queryClient.fetchQuery({
      queryKey: [ADAPTIVE_PAGE_QUERY_KEY, testId],
      queryFn: () => getCurrentPage(testId),
      staleTime: 0,
    });
}

export function isBadRequestError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 400;
}

/**
 * Submits a page. The response *is* the next page, but it is not put into the
 * page query here: the caller may first show the verdict on the answer just
 * given and only then show the next page, with `useShowPage`.
 */
export function useSubmitPage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ testId, payload }: { testId: string; payload: AdaptivePageSubmit }) =>
      submitPage(testId, payload),
    onSuccess: (_nextPage, variables) => {
      queryClient.setQueryData<AdaptiveVocabularyTest>(
        [ADAPTIVE_TEST_QUERY_KEY, variables.testId],
        (oldData) => {
          if (!oldData) return oldData;
          return { ...oldData, status: oldData.status === 'new' ? 'in-progress' : oldData.status };
        },
      );
    },
  });
}

/** Makes `page` the current page of the vocab test, as `useCurrentPage` reports it. */
export function useShowPage() {
  const queryClient = useQueryClient();
  return (testId: string, page: AdaptivePage) => {
    queryClient.setQueryData<AdaptivePage>([ADAPTIVE_PAGE_QUERY_KEY, testId], page);
  };
}

export function useUpdateAdaptiveProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      testId,
      profile,
      user,
    }: {
      testId: string;
      profile: AdaptiveUserProfile;
      user?: User | null;
    }) => updateAdaptiveProfile(testId, profile, user ?? null),
    onSuccess: (data) => {
      queryClient.setQueryData([ADAPTIVE_TEST_QUERY_KEY, data.test_id], data);
    },
  });
}

export function useCompleteAdaptiveVocabTest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ testId, user }: { testId: string; user: User | null }) =>
      completeAdaptiveVocabTest(testId, user),
    onSuccess: (result, variables) => {
      queryClient.setQueryData<AdaptiveVocabularyTest>(
        [ADAPTIVE_TEST_QUERY_KEY, variables.testId],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            status: result.status,
            shortcode: result.shortcode,
            vocab_size: result.vocab_size,
            completed_datetime: result.completed_datetime,
          };
        },
      );
    },
  });
}

export function useSharedAdaptiveResults(shortcode: string | null) {
  return useQuery({
    queryKey: ['sharedAdaptiveResults', shortcode],
    queryFn: () => fetchSharedAdaptiveResults(shortcode!),
    enabled: !!shortcode,
  });
}
